import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { HttpStatusCode } from "axios";
import jwtDecode from "jwt-decode";

import useErrors from "../../../libs/useErrors";
import {
  getLocalValue,
  removeLocalValue,
  setLocalValue,
} from "../../../libs/localStorage";

import {
  getLongTermToken,
  login,
  LoginRequest,
} from "../../../services/authServices";
import { getEssentialTerms } from "../../../services/memberServices";

import { useUserContext } from "../../../contexts/userContext";

import useLongTermLogin from "../../../hooks/useLongTermLogin";
import useBioMetrics from "../../../hooks/useBioMetrics";
import usePushKey from "../../../hooks/usePush";
import userMemberInfo from "../../../hooks/useMemberInfo";
import { useDeviceType } from "../../../hooks/useDeviceType/Index";
import useAppVersion from "../../../hooks/useAppVersion";
import useSimplePopup from "../../../hooks/useSimplePopup";
import useFullScreenLoading from "../../../hooks/useFullScreenLoading";

import CheckWithLabel from "../../../components/Input/CheckWithLabel";
import LoginInput from "../../../components/Input/Login";
import VerticalRule from "../../../components/Rule/Vertical";
import CommonButton from "../../../components/Button";
import BottomButtonGroup from "../../../components/Button/Bottom";
import DormancyPopup from "../../../components/Popup/Dormancy";
import CommonPopup from "../../../components/Popup";
import List from "../../../components/List";
import EssentialTermPopup from "../../../components/Popup/EssentialTerm";

import SandiCI from "../../../assets/img/sandi-ci.png";
import FingerPrint from "../../../assets/img/finger.png";
import FaceId from "../../../assets/img/face-id.png";
import Isms from "../../../components/Isms";

type CheckedType = {
  autoLogin: boolean;
  saveEmail: boolean;
};

export type DecodeJwtToken = {
  sub: string;
  exp: number;
  auth: string;
};

type Props = {};

// eslint-disable-next-line no-empty-pattern
export default function Login(props: Props) {
  const location = useLocation();
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  const deviceType = useDeviceType();

  const [userInfo, setUserInfo] = useState<Partial<LoginRequest>>({});
  const navigate = useNavigate();
  const [checked, setChecked] = useState<Partial<CheckedType>>({});
  const [errors, { removeError, setError }] = useErrors();
  const [isLoading, setIsLoading] = useState(false);
  const [bioResult, callBio] = useBioMetrics();
  const loadingComponent = useFullScreenLoading({
    isLoading: isLoading || (bioResult?.isLoading ?? false),
  });

  const [email, setEmail] = useState("");
  const [dormancyDate, setDormancyDate] = useState("");
  const [popup, setPopup] = useState<
    "" | "dormancy" | "bioGuide" | "essentialTerms" | "showTerms"
  >("");
  const longTermLogin = useLongTermLogin();

  const [user, { updateField }] = useUserContext();
  const [, pushCall] = usePushKey();
  const [appVersionResult, versionCall] = useAppVersion();
  const [simplePopupComponent, { open }] = useSimplePopup({});
  const { loadingUserData } = userMemberInfo({
    updateField,
    pushCall,
    appVersion: appVersionResult?.result,
    deviceType,
    open,
  });

  const clientId = "MOBILE";

  const searchParams: { [key: string]: string } = {};
  location.search
    .replace("?", "")
    .split("&")
    .forEach((item) => {
      const [key, value] = item.split("=");
      searchParams[key] = value;
    });

  const afterLoginNavigate = () => {
    if (searchParams?.redirect && !searchParams?.redirect.startsWith("/auth")) {
      navigate(
        `${decodeURIComponent(searchParams?.redirect)}${location.hash}`,
        { replace: true }
      );
    } else {
      navigate("/view", { replace: true });
    }
  };

  const onSuccessLogin = async (jwtToken: any) => {
    if (
      getLocalValue("BIO") &&
      getLocalValue("bioUser") !== userInfo.username
    ) {
      removeLocalValue("BIO");
      removeLocalValue("bioUser");
    }

    if (checked.autoLogin) {
      const res = await getLongTermToken(
        {
          accessToken: `${jwtToken.grantType} ${jwtToken.accessToken}`,
        },
        { scope: "AUTO" }
      );
      if (res.status === HttpStatusCode.Ok) {
        setLocalValue("AUTO", res.data.longTermToken);
      }
    }

    if (checked.saveEmail) {
      if (userInfo.username) {
        setLocalValue("saveEmail", userInfo.username.trim());
      }
    } else {
      removeLocalValue("saveEmail");
    }
  };

  const checkEssentialTerms = async () => {
    try {
      const result = await getEssentialTerms();
      if (result.status === HttpStatusCode.Ok) {
        if (result.data === true) {
          // nothing to do
        } else {
          setPopup("essentialTerms");
          return false;
        }
      } else {
        throw new Error();
      }
    } catch (e: any) {
      if (e?.response?.data?.error) {
        open(e?.response?.data?.error?.replace(". ", ".\n"));
      } else if (!e?.response) {
        open("서버와의 통신이 원활하지 않습니다.");
      } else {
        console.error(e.response.data);
      }
    }
    return true;
  };

  const handleLoginButton = async (e: any) => {
    e.preventDefault();
    if (!userInfo.username) {
      setError("username", "이메일 주소를 입력해주세요.");
      return;
    }

    if (!userInfo.password) {
      setError("password", "비밀번호를 입력해주세요.");
      return;
    }

    setIsLoading(true);

    try {
      const result = await login({
        ...userInfo,
        username: userInfo.username.trim(),
        clientId,
      } as LoginRequest);
      if (result.status === HttpStatusCode.Ok) {
        const decode: DecodeJwtToken = jwtDecode(result.data.accessToken);
        const subJson = JSON.parse(decode.sub);
        setLocalValue(
          "jwtToken",
          JSON.stringify({
            grantType: result.data.grantType,
            accessToken: result.data.accessToken,
            refreshToken: result.data.refreshToken,
          })
        );

        if (subJson.isDormancy) {
          setPopup("dormancy");
          setEmail(subJson.email);
          setDormancyDate(
            subJson.dormancyAt
              .split(" ")[0]
              .replace(
                /(\d{4})-(\d{2})-(\d{2})/,
                (_: any, g1: string, g2: string, g3: string) =>
                  `${g1}년 ${g2}월 ${g3}일`
              )
          );
          return;
        }

        if ((await checkEssentialTerms()) === false) {
          return;
        }
        loadingUserData();
        await onSuccessLogin(result.data);
        // afterLoginNavigate();
      } else {
        throw new Error();
      }
    } catch (e: any) {
      if (e?.response?.data?.error) {
        open(e?.response?.data?.error?.replace(". ", ".\n"));
      } else if (!e?.response) {
        open("서버와의 통신이 원활하지 않습니다.");
      } else if (e.response.status === HttpStatusCode.Unauthorized) {
        setError("password", "이메일 주소 또는 비밀번호가 일치하지 않습니다.");
      } else if (e.response.status === HttpStatusCode.NotFound) {
        setError("username", "존재하지 않는 이메일입니다.");
      } else {
        console.error(e.response.data);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (bioResult.isLoading === false) {
      if (bioResult.result === true) {
        longTermLogin("BIO", () => {
          loadingUserData();
          // afterLoginNavigate();
        });
      } else if (bioResult.result === false) {
        open("생체인증에 실패했습니다.");
      } else {
        // nothing to do
      }
    }
  }, [bioResult.isLoading, bioResult.result]);

  useEffect(() => {
    // 진입하자마자 버전을 받아온다.
    versionCall();

    const result = getLocalValue("jwtToken");
    const autoLoginToken = getLocalValue("AUTO");
    // 1. jwtToken 이 있다 -> 바로 유저 정보를 불러온다.
    if (result) {
      loadingUserData();
    }

    // 2. auto 만 있다 -> 원래처럼 longTermLogin 하고 유저 정보를 불러온다.
    if (!result && autoLoginToken) {
      longTermLogin("AUTO", () => {
        loadingUserData();
      });
    } else {
      // 3. auto 만 있는 케이스가 아니다 -> 원래 대로 해준다.(항상 이 로직을 탔을)
      longTermLogin();
    }

    const savedEmail = getLocalValue("saveEmail");
    if (savedEmail) {
      setChecked((prev) => ({ ...prev, saveEmail: true }));
      setUserInfo((prev) => ({ ...prev, username: savedEmail }));
    }

    const boarded = getLocalValue("boarded");
    if (autoLoginToken || !boarded) {
      setChecked((prev) => ({ ...prev, autoLogin: true }));
    }
  }, []);

  useEffect(() => {
    if (user && user.isLoading === false && user.checkEvacuationSituation) {
      navigate("/evacuation/situation", { replace: true });
    } else if (
      user &&
      user.isLoading === false &&
      !user.checkEvacuationSituation
    ) {
      afterLoginNavigate(); // 원래대로
    }
  }, [user]);

  const handleLoginContinue = async () => {
    const tokenString = getLocalValue("jwtToken");
    if (!tokenString) return;
  
    const jwtToken = JSON.parse(tokenString);
    loadingUserData();
    await onSuccessLogin(jwtToken);
  }

  return (
    <>
      {loadingComponent}
      <div className="min-h-content px-4 py-4 flex flex-col items-center justify-center gap-5">
        <div className="py-8">
          <img src={SandiCI} alt="sandi-ci" className="h-[100px]" />
        </div>
        <form className="w-full">
          <div className="flex flex-col gap-3 w-full">
            <div className="w-full flex flex-col gap-2.5">
              <LoginInput
                value={userInfo.username ?? ""}
                onChange={(text) => {
                  setUserInfo((prev) => ({ ...prev, username: text }));
                  removeError("username");
                }}
                onKeyDownEnter={(e) => {
                  passwordRef.current?.focus();
                }}
                autoComplete="username"
                placeholder="이메일 주소를 적어주세요"
                error={errors["username"]}
                className="rounded-t-[3px]"
                outRef={emailRef}
                type="email"
              />
              <LoginInput
                type="password"
                value={userInfo.password ?? ""}
                onChange={(text) => {
                  setUserInfo((prev) => ({ ...prev, password: text }));
                  removeError("password");
                }}
                onKeyDownEnter={(e) => {
                  handleLoginButton(e);
                }}
                autoComplete="current-password"
                placeholder="비밀번호를 적어주세요"
                error={errors["password"]}
                className="rounded-b-[3px]"
                outRef={passwordRef}
              />
            </div>
            <div className="flex justify-end gap-2.5 text-sm text-brand-neutral-60">
              <CheckWithLabel
                checked={checked.autoLogin}
                label="자동 로그인"
                onChange={(autoLogin) =>
                  setChecked((prev) => ({ ...prev, autoLogin }))
                }
                wrapperClassName="w-[auto] whitespace-nowrap"
              />
              <CheckWithLabel
                checked={checked.saveEmail}
                label="이메일 저장"
                onChange={(saveEmail) =>
                  setChecked((prev) => ({ ...prev, saveEmail }))
                }
                wrapperClassName="w-[auto] whitespace-nowrap"
              />
            </div>
            <div className="pt-2.5">
              <CommonButton
                className="w-full h-bottom-button text-base font-semibold leading-[17px]"
                onClick={handleLoginButton}
              >
                로그인
              </CommonButton>
            </div>
            <CommonButton
              variant="tertiary"
              className=" text-brand-neutral-40 w-full leading-[19px]"
              onClick={(e) => {
                e.preventDefault();
                if (getLocalValue("BIO")) {
                  callBio();
                } else {
                  setPopup("bioGuide");
                }
              }}
            >
              또는
              <span className="text-black font-medium">생체인식 로그인</span>
            </CommonButton>
            <div className="flex justify-center w-full gap-[23px] text-sm font-medium pt-[8px] pb-[6px] opacity-50 items-center leading-[17px] text-brand-neutral-60">
              <button onClick={() => navigate("/auth/findId")}>
                <div className="flex flex-col">
                  아이디 찾기
                  <div className=" bg-brand-neutral-40 h-px w-full"></div>
                </div>
              </button>
              <VerticalRule className="!h-[10px] bg-brand-neutral-30" />
              <button onClick={() => navigate("/auth/findPassword")}>
                <div className="flex flex-col">
                  비밀번호 찾기
                  <div className=" bg-brand-neutral-40 h-px w-full"></div>
                </div>
              </button>
            </div>
          </div>
        </form>
        {/* TODO: 간편로그인 미사용 숨김 */}
        {false && (
          <div className="flex flex-col w-full items-center gap-1">
            <SimpleLogin />
            <div className="flex justify-center gap-4">
              <button className="cursor-not-allowed text-gray-400">
                <LgIcon />
              </button>
            </div>
          </div>
        )}
        <Isms ISMSImgSizePixel={48} className="px-0 py-0" />
        <div className="h-[30px]" />
      </div>
      <BottomButtonGroup
        title="회원가입"
        isFixed={deviceType !== "Android"}
        onClick={(e) => {
          e.preventDefault();
          navigate("/auth/signUp");
        }}
        variant="tertiary"
        buttonClassName={
          "h-bottom-button text-base font-medium text-neutral-600 bg-brand-primary-gray-100 bg-opacity-20 border-none leading-[19px]"
        }
      />
      <DormancyPopup
        popup={popup}
        email={email}
        dormancyDate={dormancyDate}
        onClose={() => setPopup("")}
      />
      <CommonPopup
        isOpen={popup === "bioGuide"}
        onClose={() => setPopup("")}
        padding={0}
        title="생체인증 로그인 설정 안내"
        unUseHorizonRule
      >
        <div className="p-5 pt-0 text-center flex flex-col gap-2.5 items-center">
          <div>
            생체인증 로그인 설정으로
            <br />
            로그인이 더욱 안전하고 간편해 집니다.
          </div>
          <div className="flex gap-2.5 items-center justify-center">
            <img src={FaceId} alt="faceId" />
            <img src={FingerPrint} alt="fingerPrint" />
          </div>
          <div>
            생체인증 로그인 설정 방법
            <br />
            로그인 후 {">"} 내정보 {">"} 간편 로그인 설정
          </div>
          <List
            className="text-sm text-neutral-800 bg-neutral-200 p-4 rounded-lg text-start whitespace-pre-line list-outside pl-8"
            items={[
              {
                text: "아이디 도용 등의 피해방지를 위하여 본인 명의 휴대전화에서\n반드시 본인의 생체정보만 등록하시기 바랍니다.",
              },
              {
                text: "휴대전화에 등록된 모든 생체인증 수단으로 로그인이\n가능합니다.",
              },
              {
                text: "하이파인애플은 회원님의 생체정보를 저장하지 않습니다.",
              },
              {
                text: "생체인증 로그인 사용 설정은 로그인 후 가능 합니다.",
              },
            ]}
          ></List>
        </div>
        <CommonButton
          className="w-full rounded-none h-bottom-button"
          onClick={() => setPopup("")}
        >
          로그인 후 설정하기
        </CommonButton>
      </CommonPopup>

      <EssentialTermPopup popup={popup} onClose={() => setPopup("")} onSuccess={handleLoginContinue} />

      {simplePopupComponent}
    </>
  );
}

export const LgIcon = () => (
  <svg
    width="50"
    height="50"
    viewBox="0 0 50 50"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="25" cy="25" r="25" fill="#A61B39" />
    <path
      d="M36.6045 19.5H32.9602C32.8615 19.5 32.7807 19.5808 32.7807 19.6795V26.0221C32.7807 26.6342 33.2762 27.1297 33.8883 27.1297C34.5005 27.1297 34.996 26.6342 34.996 26.0239V21.245H36.5578C38.6978 21.245 40.0011 22.367 40.0011 24.8552C40.0011 27.8747 38.5146 28.7849 36.0875 28.7849H30.7036V19.6795C30.7036 19.5808 30.6228 19.5 30.5241 19.5H28.6678C28.5691 19.5 28.4883 19.5808 28.4883 19.6795V30.3199C28.4883 30.4186 28.5691 30.4994 28.6678 30.4994H36.179C39.669 30.4994 42.3241 29.1494 42.3241 25.0383C42.3241 20.9272 40.2452 19.5 36.6045 19.5Z"
      fill="white"
    />
    <path
      d="M10.1504 19.6586H7.67578V30.3414H15.2758V28.2624H10.1504V19.6586ZM20.7182 26.2704H22.7346V28.1386C22.1577 28.3268 21.5547 28.4209 20.95 28.4192C18.7228 28.4192 17.9804 27.2917 17.9804 25C17.9804 22.7083 18.6862 21.5268 20.9134 21.5268C22.1508 21.5268 22.8583 21.9154 23.4404 22.6543L24.9792 21.2427C24.0416 19.9061 22.3982 19.5 20.8611 19.5C17.3966 19.5 15.5772 21.3856 15.5772 24.9826C15.5772 28.5795 17.2206 30.5 20.8437 30.5C22.5045 30.5 24.1305 30.0765 25.014 29.4596V24.2785H20.72V26.2704H20.7182Z"
      fill="white"
    />
  </svg>
);
export const SimpleLogin = () => (
  <svg
    width="140"
    height="46"
    viewBox="0 0 140 46"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g filter="url(#filter0_d_876_19477)">
      <mask id="path-1-inside-1_876_19477" fill="white">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M36.5527 0C28.2685 0 21.5527 6.71573 21.5527 15C21.5527 23.2843 28.2685 30 36.5527 30H65.0788L70 36.2132L74.9213 30H103.447C111.732 30 118.447 23.2843 118.447 15C118.447 6.71573 111.732 0 103.447 0H36.5527Z"
        />
      </mask>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M36.5527 0C28.2685 0 21.5527 6.71573 21.5527 15C21.5527 23.2843 28.2685 30 36.5527 30H65.0788L70 36.2132L74.9213 30H103.447C111.732 30 118.447 23.2843 118.447 15C118.447 6.71573 111.732 0 103.447 0H36.5527Z"
        fill="white"
      />
      <path
        d="M65.0788 30L65.8627 29.3791L65.5624 29H65.0788V30ZM70 36.2132L69.2161 36.8341L70 37.8238L70.7839 36.8341L70 36.2132ZM74.9213 30V29H74.4376L74.1374 29.3791L74.9213 30ZM22.5527 15C22.5527 7.26801 28.8207 1 36.5527 1V-1C27.7162 -1 20.5527 6.16344 20.5527 15H22.5527ZM36.5527 29C28.8207 29 22.5527 22.732 22.5527 15H20.5527C20.5527 23.8366 27.7162 31 36.5527 31V29ZM65.0788 29H36.5527V31H65.0788V29ZM64.2949 30.6209L69.2161 36.8341L70.7839 35.5923L65.8627 29.3791L64.2949 30.6209ZM70.7839 36.8341L75.7052 30.6209L74.1374 29.3791L69.2161 35.5923L70.7839 36.8341ZM103.447 29H74.9213V31H103.447V29ZM117.447 15C117.447 22.732 111.179 29 103.447 29V31C112.284 31 119.447 23.8366 119.447 15H117.447ZM103.447 1C111.179 1 117.447 7.26801 117.447 15H119.447C119.447 6.16344 112.284 -1 103.447 -1V1ZM36.5527 1H103.447V-1H36.5527V1Z"
        fill="#DBDBDB"
        mask="url(#path-1-inside-1_876_19477)"
      />
    </g>
    <g clipPath="url(#clip0_876_19477)">
      <path
        d="M51.0664 12.707H52.6367V13.4805H51.0664V17.1602H50.1406V9.53125H51.0664V12.707ZM42.9336 15.2148C43.7695 14.9531 44.5 14.6191 45.125 14.2129C45.75 13.8066 46.2441 13.3398 46.6074 12.8125C46.9746 12.2852 47.1895 11.7188 47.252 11.1133H43.3672V10.3398H48.2539C48.2539 11.2188 48.0547 12.0254 47.6562 12.7598C47.2578 13.4941 46.6895 14.1309 45.9512 14.6699C45.2129 15.209 44.3398 15.6367 43.332 15.9531L42.9336 15.2148ZM44.5391 16.3867H45.4531V18.9531H51.5469V19.7266H44.5391V16.3867ZM61.9297 17.2891H61.0039V14.2656H59.2227V13.4922H61.0039V12.2852H59.2227V11.5234H61.0039V9.53125H61.9297V17.2891ZM53.3164 14.9219C53.5742 14.9219 54.0234 14.918 54.6641 14.9102V11.1367H53.6094V10.3867H59.1406V11.1367H58.0742V14.7871C58.5664 14.7559 59.0352 14.7148 59.4805 14.6641L59.5273 15.3555C58.6289 15.4922 57.6719 15.584 56.6562 15.6309C55.6445 15.6777 54.5703 15.7031 53.4336 15.707L53.3164 14.9219ZM55.2148 16.6445H56.1406V18.9531H62.2109V19.7266H55.2148V16.6445ZM55.5547 14.8926C56.1055 14.8809 56.6484 14.8633 57.1836 14.8398V11.1367H55.5547V14.8926ZM76.2148 18.6602H66.6758V17.8867H70.9648V15.918H67.8711V12.707H74.1055V11.0664H67.8477V10.3047H75.0078V13.457H68.7852V15.1562H75.2539V15.918H71.8672V17.8867H76.2148V18.6602ZM85.2617 11.6875C85.2617 12.2852 85.2539 12.8066 85.2383 13.252C85.2266 13.6973 85.1934 14.2168 85.1387 14.8105C85.084 15.4004 84.9961 16.0469 84.875 16.75L83.9375 16.6562C84.0664 15.9961 84.1621 15.375 84.2246 14.793C84.2871 14.207 84.3242 13.6914 84.3359 13.2461C84.3516 12.7969 84.3594 12.2773 84.3594 11.6875V11.4062H78.0781V10.6445H85.2617V11.6875ZM77.0469 17.6523H86.5391V18.4141H77.0469V17.6523ZM96.0195 17.1602H95.1055V9.54297H96.0195V17.1602ZM87.6641 12.8242C87.6641 12.332 87.7812 11.8926 88.0156 11.5059C88.2539 11.1191 88.5801 10.8164 88.9941 10.5977C89.4121 10.3789 89.8789 10.2695 90.3945 10.2695C90.9102 10.2695 91.375 10.3789 91.7891 10.5977C92.2031 10.8164 92.5293 11.1191 92.7676 11.5059C93.0059 11.8926 93.125 12.332 93.125 12.8242C93.125 13.3203 93.0059 13.7637 92.7676 14.1543C92.5293 14.5449 92.2031 14.8496 91.7891 15.0684C91.375 15.2832 90.9102 15.3906 90.3945 15.3906C89.8789 15.3906 89.4121 15.2832 88.9941 15.0684C88.5801 14.8496 88.2539 14.5449 88.0156 14.1543C87.7812 13.7637 87.6641 13.3203 87.6641 12.8242ZM88.5664 12.8242C88.5625 13.1641 88.6387 13.4688 88.7949 13.7383C88.9551 14.0039 89.1738 14.2109 89.4512 14.3594C89.7324 14.5078 90.0469 14.582 90.3945 14.582C90.7383 14.582 91.0488 14.5078 91.3262 14.3594C91.6035 14.2109 91.8223 14.0039 91.9824 13.7383C92.1426 13.4688 92.2227 13.1641 92.2227 12.8242C92.2227 12.4883 92.1426 12.1895 91.9824 11.9277C91.8223 11.6621 91.6035 11.4551 91.3262 11.3066C91.0488 11.1543 90.7383 11.0781 90.3945 11.0781C90.0469 11.0781 89.7324 11.1523 89.4512 11.3008C89.1738 11.4492 88.9551 11.6562 88.7949 11.9219C88.6387 12.1875 88.5625 12.4883 88.5664 12.8242ZM89.293 16.3867H90.207V18.9531H96.3477V19.7266H89.293V16.3867Z"
        fill="#373D3F"
      />
    </g>
    <defs>
      <filter
        id="filter0_d_876_19477"
        x="19.5527"
        y="0"
        width="104.895"
        height="45.2129"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dx="2" dy="5" />
        <feGaussianBlur stdDeviation="2" />
        <feComposite in2="hardAlpha" operator="out" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0"
        />
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow_876_19477"
        />
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow_876_19477"
          result="shape"
        />
      </filter>
      <clipPath id="clip0_876_19477">
        <rect width="92" height="17" fill="white" transform="translate(24 5)" />
      </clipPath>
    </defs>
  </svg>
);
